import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { RecordingService } from "app/js/src/services/recording/recording.service";
import { Subscription } from "rxjs";
import {
  RecordingJob,
  RecordingJobStatus,
} from "app/js/src/domain/recording-job";
import { RecordingSource } from "app/js/src/domain/recording-source";
import moment from "moment";
import { ColDef, ColGroupDef, GridApi, GridOptions } from "ag-grid-community";
import { RecordingJobActionsComponent } from "./recording-job-actions/recording-job-actions.component";
import { UI } from "app/js/src/services/ui.service";
import { RecordingJobSourcesComponent } from "./recording-job-sources/recording-job-sources.component";
import { Router } from "@angular/router";

@Component({
  selector: "evrideo-recording-scheduler",
  templateUrl: "./recording-scheduler.component.html",
  styleUrls: ["./recording-scheduler.component.scss"],
})
export class RecordingSchedulerComponent implements OnInit, OnDestroy {
  @Input() jobs: RecordingJob[] = [];
  hidePagination = true;
  scheduledJobsChanged: Subscription;
  scheduledJobCreated: Subscription;
  scheduledJobUpdated: Subscription;
  scheduledJobDeleted: Subscription;
  createNewRecordingJobSubscription: Subscription;
  gridOptions: GridOptions;
  inEditModeNode: any;
  inEditModeData: any;
  gridApi: GridApi;
  gridColumnApi: unknown;
  columnDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: "Name",
      field: "name",
      suppressSizeToFit: true,
    },
    {
      headerName: "Record Resources",
      cellRenderer: RecordingJobSourcesComponent,
      sortable: false,
    },
    {
      headerName: "Start Time",
      field: "startTime",
      sort: "asc",
      cellRenderer: this.formatDateTime.bind(this),
    },
    {
      headerName: "End Time",
      field: "endTime",
      cellRenderer: this.formatDateTime.bind(this),
    },
    {
      headerName: "Duration",
      cellRenderer: this.formatDuration,
    },
    {
      headerName: "Status",
      field: "jobStatus",
      cellRenderer: this.formatJobStatus,
    },
    {
      colId: "actions",
      sortable: false,
      cellRenderer: "jobActionsComponent",
    },
  ];
  showManageJobOverlay = false;
  recordingJobToManage: any;
  localTimezone: string;

  constructor(
    private recordingService: RecordingService,
    private ui: UI,
    private router: Router,
  ) {}

  ngOnInit() {
    this.localTimezone = moment.tz.guess();
    this.gridOptions = <GridOptions>{
      context: { componentParent: this },
      defaultColDef: {
        resizable: false,
        lockPosition: true,
        sortable: true,
        cellStyle: { "line-height": "44px" },
      } as ColDef,
      rowHeight: 44,
      animateRows: true,
      paginationAutoPageSize: false,
      paginationPageSize: 15,
      paginationPageSizeSelector: false,
      pagination: true,
      getRowHeight: (e) => {
        let height = 44;
        if (e.data) {
          const sourcesCount = (e.data as RecordingJob).recordingSources.length;
          if (sourcesCount > 1) {
            height += (sourcesCount - 1) * 14;
          }
        }
        return height;
      },
      components: {
        jobActionsComponent: RecordingJobActionsComponent,
      },
      getRowClass: this.styleRow,
    };
  }
  ngOnDestroy(): void {
    if (this.scheduledJobsChanged) {
      this.scheduledJobsChanged.unsubscribe();
    }

    if (this.scheduledJobCreated) {
      this.scheduledJobCreated.unsubscribe();
    }
    if (this.scheduledJobUpdated) {
      this.scheduledJobUpdated.unsubscribe();
    }
    if (this.scheduledJobDeleted) {
      this.scheduledJobDeleted.unsubscribe();
    }
    if (this.createNewRecordingJobSubscription) {
      this.createNewRecordingJobSubscription.unsubscribe();
    }
  }
  loadData() {
    this.recordingService
      .get(
        null,
        null,
        null,
        null,
        null,
        [RecordingJobStatus.Scheduled, RecordingJobStatus.Running],
        true,
      )
      .then((jobs) => {
        this.gridApi.setGridOption("rowData", jobs);
        this.afterGridUpdateTrigger();
      });
  }
  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
    this.scheduledJobCreated = this.recordingService
      .getRecordingJobCreatedObserver()
      .subscribe(this.createJobInGrid.bind(this));
    this.scheduledJobUpdated = this.recordingService
      .getRecordingJobUpdatedObserver()
      .subscribe(this.updateJobInGrid.bind(this));
    this.scheduledJobDeleted = this.recordingService
      .getRecordingJobDeletedObserver()
      .subscribe(this.deleteJobInGrid.bind(this));
    this.scheduledJobsChanged = this.recordingService
      .getRecordingJobCacheUpdateObserver()
      .subscribe(this.loadData.bind(this));
    this.createNewRecordingJobSubscription = this.recordingService
      .getCreateNewRecordingJobObserver()
      .subscribe(() => {
        this.openJobSettingsWindow(null, null);
      });
    this.afterGridUpdateTrigger();
  }
  styleRow(params: any) {
    if (
      (params.node.data.jobStatus as RecordingJobStatus) ===
      RecordingJobStatus.Running
    ) {
      console.log(params.node.data.name, "is running");
      return "running-status";
    }
  }
  afterGridUpdateTrigger() {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.refreshCells();
    this.validatePagination();
  }
  validatePagination() {
    const pages = this.gridApi.paginationGetTotalPages();
    if (pages && pages > 1) {
      this.hidePagination = false;
    } else {
      this.hidePagination = true;
    }
  }
  createJobInGrid(id: string) {
    this.updateRowInGrid(id, "add");
  }
  updateJobInGrid(id: string) {
    this.updateRowInGrid(id, "update");
  }
  deleteJobInGrid(id: string) {
    this.updateRowInGrid(id, "remove");
  }
  updateRowInGrid(id: string, action: string) {
    console.log("recording-scheduler->updateRowInGrid-> action: ", action);
    this.reloadCurrentRoute().then();

    /*if (!id) { return; }
    const data = {};
    data[action] = [];
    const rowNode = this.gridApi.getRowNode(id);
    if (action === 'remove' && rowNode) {
      data['remove'] = [rowNode.data];
      this.gridApi.updateRowData(data);
      this.afterGridUpdateTrigger();
    } else {
      this.recordingService.get(null, null, null, null, id, null)
        .then(x => x[0])
        .then(job => {
          if (job.endTime.valueOf() < moment.utc().valueOf()) {
            action = 'remove';
          }
          data[action] = [job];
          this.gridApi.updateRowData(data);
          this.gridApi.refreshCells({ columns: ['actions'], force: true });
          this.afterGridUpdateTrigger();
        })
        .catch();
    }*/
  }
  deleteJob(recordingJob: RecordingJob): void {
    this.recordingService
      .deleteRecordingJob(recordingJob)
      .then((result) => console.log(`delete: ${result}`))
      .catch((err) => console.log(`delete error: ${err}`));
  }

  toggleJobRecording(recordingJob: RecordingJob): void {
    let action: string, callback;
    if (recordingJob.isRunning()) {
      action = "stop";
      callback = () => {
        recordingJob.endTime = moment.utc();
        recordingJob.jobStatus = RecordingJobStatus.Stopped;
        this.sendUpdateRecordingJob(recordingJob);
      };
    } else {
      action = "start";
      callback = () => {
        recordingJob.startTime = moment.utc();
        recordingJob.jobStatus = RecordingJobStatus.Scheduled;
        this.sendUpdateRecordingJob(recordingJob);
      };
    }
    this.ui.notify(this.ui.EVENTS.ShowConfirm, {
      title: `${action} recording`,
      message: `Are you sure that you want to ${action} recording ${recordingJob.name}`,
      buttons: [
        {
          title: `Yes, ${action} recording!`,
          callback: callback,
        },
        {
          title: "No",
          callback: () => {},
        },
      ],
    });
  }

  sendUpdateRecordingJob(recordingJob: RecordingJob): void {
    this.recordingService
      .updateRecordingJob(recordingJob)
      .then((result) => console.log(`update: ${result}`))
      .catch((err) => console.log(`update error: ${err}`));
  }

  showJobSettings(id): void {
    const node = this.gridApi.getRowNode(id);
    this.openJobSettingsWindow(id, node);
  }

  openJobSettingsWindow(id: string, node) {
    this.gridOptions.context["manageId"] = id;
    if (!node) {
      this.recordingJobToManage = this.recordingService.initEmptyRecordingJob();
    } else {
      this.recordingJobToManage = node.data;
    }
    this.showManageJobOverlay = true;
  }

  saveJobSettings(recordingJob: RecordingJob) {
    this.showManageJobOverlay = false;
    if (!recordingJob) {
      return;
    }
    const id = this.gridOptions.context["manageId"];
    if (id) {
      this.gridOptions.context["manageId"] = undefined;
      this.recordingService
        .updateRecordingJob(recordingJob)
        .then((result) => console.log(`update: ${result}`))
        .catch((err) => console.log(`update error: ${err}`));
    } else {
      this.recordingService
        .createRecordingJob(recordingJob)
        .then((result) => console.log(`create: ${result}`))
        .catch((err) => console.log(`create error: ${err}`));
    }
  }

  JobDateUpdated(index, type, date: moment.Moment): void {
    console.log(`index:${index}; type:${type}; date:${date.format()}`);
  }

  formatDateTime(param: any) {
    return (param.value as moment.Moment)
      .tz(this.localTimezone)
      .format("DD/MM/YYYY HH:mm");
  }
  formatDuration(param: any) {
    const recordingJob = param.data as RecordingJob;
    const diff = recordingJob.endTime.diff(recordingJob.startTime);
    const hours = Math.floor(moment.duration(diff).asHours());
    return moment(diff).format(`[${hours}h]mm[m00s]`);
  }
  streamingSourceNames(param: any) {
    const names = [];
    (param.value as RecordingSource[]).forEach((source) => {
      names.push(source.name);
    });
    return names.join(", ");
  }
  reloadCurrentRoute() {
    const currentUrl = this.router.url;
    return this.router
      .navigateByUrl("/", { skipLocationChange: true })
      .then(() => {
        this.router.navigate([currentUrl]);
      });
  }
  formatJobStatus(param: any) {
    switch (param.value as RecordingJobStatus) {
      case RecordingJobStatus.Running:
        return "Running";
      case RecordingJobStatus.Finished:
        return "Finished";
      case RecordingJobStatus.Scheduled:
        return "Scheduled";
      case RecordingJobStatus.Stopped:
        return "Stopped";
    }
  }
}
